<template>
  <h4>Try a different payment method</h4>
  <div id="creditcardfields">
    <div>
      <div class="field">
        <label
          class="tinylabel"
          :class="{ validationlabel: hasErrors('nameOnCard') }"
          >Name on Card <red v-pre>*</red></label
        ><input
          v-model="paymentMethod.nameOnCard"
          type="text"
          name="nameOnCard"
          :class="{ validationfield: hasErrors('nameOnCard') }"
          @input="validatedFieldChanged"
        />
        <validation-error
          name="nameOnCard"
          :errors="validationErrors"
        ></validation-error>
      </div>
      <br />
      <div class="fieldleft">
        <label
          class="tinylabel"
          :class="{ validationlabel: hasErrors('cardNumber') }"
          >Card Number <red v-pre>*</red></label
        >
        <input
          v-model="paymentMethod.cardNumber"
          v-maska="{ mask: '################' }"
          type="text"
          name="cardNumber"
          :class="{ validationfield: hasErrors('cardNumber') }"
          @input="validatedFieldChanged"
        />
        <validation-error
          name="cardNumber"
          :errors="validationErrors"
        ></validation-error>
      </div>
      <div class="fieldright">
        <img
          :src="require('@/assets/images/creditcards-logo.png')"
          width="253"
          height="48"
          alt="Credit Cards Payment Method"
        />
      </div>
      <div style="clear: both"></div>
      <div class="fieldleft">
        <label class="tinylabel" :class="{ validationlabel: hasErrors('cvv') }"
          >CVC <red v-pre>*</red></label
        ><input
          v-model="paymentMethod.cvv"
          v-maska="{ mask: '####' }"
          type="text"
          name="cvv"
          :class="{ validationfield: hasErrors('cvv') }"
          @input="validatedFieldChanged"
        />
        <hint v-pre style="font-size: 12px"
          >This is the 3 or 4 digit number on the back of the card.</hint
        >
        <validation-error
          name="cvv"
          :errors="validationErrors"
        ></validation-error>
      </div>
      <div class="fieldright">
        <label
          class="tinylabel"
          :class="{ validationlabel: hasErrors('expDateMonth') }"
          >Expiration Date <red v-pre>*</red></label
        >
        <div style="width: 40%; float: left; margin-right: 1%">
          <select
            v-model="paymentMethod.expDateMonth"
            class="plainfieldselect"
            placeholder="Select Month"
            name="expDateMonth"
            :class="{ validationfield: hasErrors('expDateMonth') }"
            @change="validatedFieldChanged"
          >
            <option v-for="month in cardMonths" :key="month" :value="month">
              {{ month }}
            </option>
          </select>
          <validation-error
            name="expDateMonth"
            :errors="validationErrors"
          ></validation-error>
        </div>
        <div style="width: 50%; float: left">
          <select
            v-model="paymentMethod.expDateYear"
            class="plainfieldselect"
            placeholder="Select Year"
            name="expDateYear"
            :class="{ validationfield: hasErrors('expDateYear') }"
            @change="validatedFieldChanged"
          >
            <option v-for="year in cardYears" :key="year" :value="year">
              {{ year }}
            </option>
          </select>
          <validation-error
            name="expDateYear"
            :errors="validationErrors"
            style="float: right"
          ></validation-error>
        </div>
      </div>
    </div>
    <div style="clear: both"></div>
    <center>
      <button :class="{ disabled: !validated }" @click="changePaymentInfo()">
        Submit
      </button>
    </center>
  </div>
</template>
<script lang="ts" setup>
import { PaymentMethod } from "@/models/PaymentMethod";
import ValidationError from "@/components/ValidationError.vue";
import { validateCard, validateCVV } from "@/utils/CreditCards";
import {
  defineComponent,
  Ref,
  ref,
  onMounted,
  computed,
  ComputedRef,
} from "vue";

const paymentMethod = ref(new PaymentMethod());
const validationErrors: Ref<any[]> = ref([]);
const emit = defineEmits(["payment-changed"]);

onMounted(() => {
  paymentMethod.value = new PaymentMethod();
});
function createValidationMessages(): void {
  if (paymentMethod.value.creditCard) {
    let creditCardValidated = false;

    if (!paymentMethod.value.nameOnCard) {
      const error = {
        name: "nameOnCard",
        value: "Name on card is required.",
      };
      validationErrors.value.push(error);
    }
    if (!paymentMethod.value.cardNumber) {
      const error = {
        name: "cardNumber",
        value: "Card number is required.",
      };
      validationErrors.value.push(error);
    } else {
      creditCardValidated = validateCard(paymentMethod.value.cardNumber);
      if (!creditCardValidated) {
        const error = {
          name: "cardNumber",
          value:
            "Entered card is not supported. We support Master card, Visa, American Express and Discover.",
        };
        validationErrors.value.push(error);
      }
    }
    if (!paymentMethod.value.cvv) {
      const error = {
        name: "cvv",
        value: "CVC is required.",
      };
      validationErrors.value.push(error);
    } else if (creditCardValidated) {
      if (
        !validateCVV(paymentMethod.value.cardNumber, paymentMethod.value.cvv)
      ) {
        const error = {
          name: "cvv",
          value: "Invalid CVC number.",
        };
        validationErrors.value.push(error);
      }
    }
    if (!paymentMethod.value.expDateMonth) {
      const error = {
        name: "expDateMonth",
        value: "Month is required.",
      };
      validationErrors.value.push(error);
    }
    if (!paymentMethod.value.expDateYear) {
      const error = {
        name: "expDateYear",
        value: "Year is required.",
      };
      validationErrors.value.push(error);
    }
  }
}
function clearValidationMessage(name: string): void {
  const elements = validationErrors.value.filter((element) => {
    return element.name == name;
  });
  elements.forEach((element) => {
    const index = validationErrors.value.indexOf(element);
    validationErrors.value.splice(index, 1);
  });
}

function validatedFieldChanged(event: any): void {
  clearValidationMessage(event.target.name);
}

function hasErrors(name: string): boolean {
  return (
    validationErrors.value.find((element) => {
      return element.name == name;
    }) != null
  );
}

function changePaymentInfo() {
  if (!validated.value) {
    createValidationMessages();
    return;
  }
  emit("payment-changed", paymentMethod);
  //closeModal("ChangePaymentInfoModal", modalPop);
}
//This function is not used
// function onShow() {
//   // Reset user on new show
//   paymentMethod.value = new PaymentMethod();
// }

const cardYears: ComputedRef<string[]> = computed(() => {
  const result = [];
  const currentYear = new Date().getFullYear();
  for (let i = currentYear; i < currentYear + 20; i++) {
    result.push(i.toString());
  }
  return result;
});

const cardMonths: ComputedRef<string[]> = computed(() => {
  const result = [];
  for (let i = 1; i <= 12; i++) {
    result.push(pad(i, 2));
  }
  return result;
});

function pad(num: number, size: number) {
  let res = num.toString();
  while (res.length < size) res = "0" + num;
  return res;
}

const validated = computed(() => {
  let result = true;

  if (paymentMethod.value.creditCard) {
    const crediCardValidated =
      Boolean(paymentMethod.value.nameOnCard) &&
      Boolean(paymentMethod.value.cardNumber) &&
      Boolean(paymentMethod.value.cvv) &&
      Boolean(paymentMethod.value.expDateMonth) &&
      Boolean(paymentMethod.value.expDateYear);
    result = result && crediCardValidated;

    if (result) {
      const creditCardValidated = validateCard(paymentMethod.value.cardNumber);
      if (!creditCardValidated) {
        return false;
      }
      result =
        result &&
        validateCVV(paymentMethod.value.cardNumber, paymentMethod.value.cvv);
    }
  }
  if (!result) {
    return false;
  }

  return result;
});
</script>
